import { PostgrestError, PostgrestSingleResponse } from '@supabase/supabase-js'
import { useCallback, useState } from 'react'
import useSWR, { SWRConfiguration, SWRResponse } from 'swr'
import { useSupabase } from '../context/supabaseContext'

export interface UseSwrRecord<T> extends SWRResponse<T, Error> {
  delete: () => Promise<PostgrestSingleResponse<void>>
  update: (data: Partial<T>) => Promise<PostgrestSingleResponse<T>>
}

interface UseDataOptions<T> extends SWRConfiguration {
  key: string
  queryFn: () => Promise<T>
  addFn?: (data: T) => Promise<T>
  swrOptions?: SWRConfiguration
}

export interface UseDataState {
  loading: boolean
  updating: boolean
  deleting: boolean
  error: Error | PostgrestError | null
  updateError: Error | PostgrestError | null
  deleteError: Error | PostgrestError | null
}

const useData = <T>(
  options: UseDataOptions<T>
): SWRResponse<T, Error> & {
  refetch: () => void
  mutate: (
    data: T | Promise<T> | undefined,
    shouldRevalidate?: boolean
  ) => Promise<T | undefined>
  callRpc: (fnName: string, params?: object) => Promise<any>
  state: UseDataState
} => {
  const { key, queryFn, ...swrOptions } = options
  const { supabase } = useSupabase()
  const fetcher = async () => await queryFn()

  const swrResponse = useSWR<T, Error>(key, fetcher, swrOptions)

  const [state, setState] = useState<UseDataState>({
    loading: !swrResponse.data && !swrResponse.error,
    updating: false,
    deleting: false,
    error: null,
    updateError: null,
    deleteError: null
  })

  const refetch = useCallback(() => {
    return swrResponse.mutate()
  }, [swrResponse])

  const callRpc = useCallback(
    async (fnName: string, params: object = {}) => {
      setState((prevState) => ({ ...prevState, loading: true, error: null }))
      const { data, error } = await supabase.rpc(fnName, params)
      setState((prevState) => ({ ...prevState, loading: false, error }))
      if (error) {
        throw new Error(error.message)
      }
      return data
    },
    [supabase]
  )

  return {
    ...swrResponse,
    refetch,
    callRpc,
    state: {
      ...state,
      loading:
        swrResponse.isValidating || (!swrResponse.data && !swrResponse.error)
    }
  }
}

interface UseRecordOptions<T> extends UseDataOptions<T> {
  id?: string | number
}

export const useSWRRecord = <T>(
  options: UseRecordOptions<T>
): SWRResponse<T, Error> & {
  upsert: (data: T) => Promise<void>
  delete: () => Promise<void>
  state: UseDataState
} => {
  const { id, key, queryFn, ...swrOptions } = options
  const { supabase } = useSupabase()
  const recordKey = `${key}-${id}`

  const swrResponse = useData<T>({
    key: recordKey,
    queryFn,
    ...swrOptions
  })

  const [state, setState] = useState<UseDataState>({
    loading: false,
    updating: false,
    deleting: false,
    error: null,
    updateError: null,
    deleteError: null
  })

  const upsert = useCallback(
    async (data: T) => {
      setState((prevState) => ({
        ...prevState,
        updating: true,
        updateError: null
      }))
      const { error } = await supabase.from(key).upsert(data)
      setState((prevState) => ({
        ...prevState,
        updating: false,
        updateError: error
      }))
      if (error) {
        throw new Error(error.message)
      }
      swrResponse.mutate()
    },
    [key, supabase, swrResponse]
  )

  const deleteRecord = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      deleting: true,
      deleteError: null
    }))
    const { error } = await supabase.from(key).delete().eq('id', id)
    setState((prevState) => ({
      ...prevState,
      deleting: false,
      deleteError: error
    }))
    if (error) {
      throw new Error(error.message)
    }
    swrResponse.mutate()
  }, [id, key, supabase, swrResponse])

  return {
    ...swrResponse,
    upsert,
    delete: deleteRecord,
    state: {
      ...swrResponse.state,
      ...state,
      loading: swrResponse.isValidating || state.loading
    }
  }
}

export const useSupaSWR = <T>(
  options: UseDataOptions<T>
): SWRResponse<T, Error> & {
  add: (data: T) => Promise<T>
  delete: (id: string | number) => Promise<void>
  state: UseDataState
} => {
  const { key, queryFn, ...swrOptions } = options
  const { supabase } = useSupabase()

  const swrResponse = useData<T>({
    key,
    queryFn,
    ...swrOptions
  })

  const [state, setState] = useState<UseDataState>({
    loading: false,
    updating: false,
    deleting: false,
    error: null,
    updateError: null,
    deleteError: null
  })

  const add = async (data: T) => {
    setState((prevState) => ({
      ...prevState,
      updating: true,
      updateError: null
    }))

    const r = await supabase.from(key).upsert(data).single<T>()

    setState((prevState) => ({
      ...prevState,
      updating: false,
      updateError: r.error
    }))

    if (r.error) {
      throw new Error(r.error.message)
    }

    swrResponse.mutate()
    return r.data
  }

  const deleteItem = useCallback(
    async (id: string | number) => {
      setState((prevState) => ({
        ...prevState,
        deleting: true,
        deleteError: null
      }))
      const { error } = await supabase.from(key).delete().eq('id', id)
      setState((prevState) => ({
        ...prevState,
        deleting: false,
        deleteError: error
      }))
      if (error) {
        throw new Error(error.message)
      }
      void swrResponse.mutate()
    },
    [key, supabase, swrResponse]
  )

  return {
    ...swrResponse,
    add,
    delete: deleteItem,
    state: {
      ...swrResponse.state,
      ...state,
      loading: swrResponse.isValidating || state.loading
    }
  }
}
